import React from 'react';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';

/**
 * Componente reutilizable que muestra la tabla de términos de cómputo de Kubiy
 * Muestra los diferentes tamaños de instancias con la nomenclatura de Kubiy
 * Soporta inglés y español vía i18n.
 */
const KubiyComputeSizesTable: React.FC = () => {
  const { t } = useTranslation();

  const rows = [
    { key: 'edge', aws: 't3.nano', vcpu: '2', ram: '0.5 GB' },
    { key: 'core', aws: 't3.micro', vcpu: '2', ram: '1 GB' },
    { key: 'pulse', aws: 't3.small', vcpu: '2', ram: '2 GB' },
    { key: 'forge', aws: 't3.medium', vcpu: '2', ram: '4 GB' },
    { key: 'titan', aws: 't3.large+', vcpu: '2+', ram: '8+ GB' },
  ] as const;

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
      <details className="group">
        <summary className="cursor-pointer flex items-center justify-between text-sm font-semibold text-gray-900 dark:text-white">
          <span className="flex items-center gap-2">
            <InfoCircleOutlined className="text-blue-600 dark:text-blue-400" />
            <span>{t('kubiyComputeSizes.title')}</span>
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 group-open:hidden">{t('kubiyComputeSizes.expand')}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 hidden group-open:inline">{t('kubiyComputeSizes.collapse')}</span>
        </summary>
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
            {t('kubiyComputeSizes.intro')}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold text-gray-900 dark:text-white">{t('kubiyComputeSizes.columnKubiyName')}</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold text-gray-900 dark:text-white">{t('kubiyComputeSizes.columnMentalModel')}</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold text-gray-900 dark:text-white">{t('kubiyComputeSizes.columnAwsEquivalent')}</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold text-gray-900 dark:text-white">{t('kubiyComputeSizes.columnVcpu')}</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold text-gray-900 dark:text-white">{t('kubiyComputeSizes.columnRam')}</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold text-gray-900 dark:text-white">{t('kubiyComputeSizes.columnUseCase')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.key} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 font-mono font-semibold text-blue-600 dark:text-blue-400">ky.{row.key}</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-700 dark:text-gray-300">{t(`kubiyComputeSizes.${row.key}.mentalModel`)}</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-700 dark:text-gray-300">{row.aws}</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-700 dark:text-gray-300">{row.vcpu}</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-700 dark:text-gray-300">{row.ram}</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-700 dark:text-gray-300">{t(`kubiyComputeSizes.${row.key}.useCase`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 italic">
            {t('kubiyComputeSizes.note')}
          </p>
        </div>
      </details>
    </div>
  );
};

export default KubiyComputeSizesTable;
